import { Cheque } from '@baemingo/table-events/dist/types';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { DragTypes } from '~ComponentsImproved/TSR/Types';
import { useBridgeApi, useSelector, useStaffCan } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { isSplitChequeCrashFixCompany, runDndDrop } from '~splitChequeCrashFix';
import { TableKey } from './types';
import { useOrderStatusApi, useOrderStatusUniverse } from '~Components/OrderStatusUniverse';
import { GetTables_Venue_floorPlans_tables as TableType } from '~Scenes/Venue/__queries__';
import styled from '@emotion/styled';

const Wrapper = styled.div<{
  selected: boolean;
  active: boolean;
  isOver: boolean;
  staffCanMutate: boolean;
  hasPrintedCheque: boolean;
  hasRoundShape: boolean; // If true, will display the table as round, regardless of what view the tables are displayed in
  shape?: number; // Table shapes: 0 = square, 1 = circle, 2 = long laying table, 3 = long standing table, 4 = large circle
}>`
  margin: 10px;
  padding: 8px;
  height: ${({ shape }) => {
    switch (shape) {
      case 3:
        return '98px'; // Lowered from 117px (iPad) to 98px (handy)
      case 4:
        return '64px'; // Lowered from 76px (iPad) to 64px (handy)
      default:
        return '48px'; // Standard handy
    }
  }};
  width: ${({ shape }) => {
    switch (shape) {
      case 2:
        return '98px'; // Lowered from 117px (iPad) to 98px (handy)
      case 4:
        return '64px'; // Lowered from 76px (iPad) to 64px (handy)
      default:
        return '48px'; // Standard handy
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: ${({ selected }) => (selected ? '1px solid #ffc000' : '1px solid transparent')};
  border-radius: ${({ hasRoundShape }) => (hasRoundShape ? '50%' : '4px')};

  ${({ hasPrintedCheque }) =>
    hasPrintedCheque &&
    `
    box-shadow: 0px 0px 0px 4px rgba(150, 100, 100, 0.8) inset;
  `}

  ${({ isOver, staffCanMutate }) =>
    isOver &&
    staffCanMutate &&
    `
    box-shadow: 0px 0px 15px 15px rgba(255,255,255,0.6);
  `}

  position: relative;
  font-size: 20px;
  font-weight: 600;
  line-height: 20px;
  background: ${({ active, isOver }) => (active || isOver ? '#d5d7d8' : '#545960')};
  color: ${(props) => (props.active ? '#127843' : '#1c232c')};
  opacity: ${({ staffCanMutate }) => (staffCanMutate ? 1 : 0.6)};

  // selected state
  ${(props) => props.selected && 'background: #0e1216; color: #fff'}
`;

// Inner flex column so table name and seating time stack without overlapping
const TableNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1;
`;

const SeatingTime = styled.span`
  font-size: 9px;
  font-weight: 400;
  line-height: 9px;
  color: inherit;
  opacity: 0.8;
  white-space: nowrap;
`;

const CourseType = styled.div<{ color: string }>`
  position: absolute;
  top: -10px;
  left: -10px;
  width: 24px;
  aspect-ratio: 1;
  border-radius: 50%;
  background-color: ${({ color }) => (color ? color : '#fff')};
  font-size: 12px;
  color: var(--colorBlueGray1300);
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  keyType?: TableKey;
  table: TableType;
  onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: (table: TableType, keyType?: TableKey) => void;
  onDoubleClick?: (table: TableType) => void;
  children: React.ReactNode;
  selected: boolean;
  onSelectCheque: (cheque: Cheque) => void;
  onSelectTable: (table: TableType) => void;
  isMapView?: boolean;
}

const Table: React.FC<Props> = ({
  keyType,
  onSelectCheque,
  onSelectTable,
  selected,
  table,
  onTouchStart,
  onTouchEnd,
  onDoubleClick,
  children,
  isMapView,
}) => {
  const universe = useTableUniverse();
  const api = useTableApi();
  const bridgeApi = useBridgeApi();
  const staffCan = useStaffCan();
  const [{ showSeatingTimeInTSR }] = useStorage();
  const venueId = useSelector((state) => state.venueId!);
  const orderStatusApi = useOrderStatusApi(venueId);
  // only defer the drop mutation for the affected company
  const useFix = isSplitChequeCrashFixCompany(useSelector((state) => state.company));

  const chequesForTable = universe.cheques.filter((c) => c.tableId === table?.id);
  const currentCourseType = chequesForTable.find((c) => c.stage)?.stage ?? null;
  const hasPrintedCheque = chequesForTable.some((c) => c.printed);

  const courseTypes = useSelector((state) => state.courseTypes);

  const orderStatusUniverse = useOrderStatusUniverse();

  const orders = orderStatusUniverse.orders.filter(
    (o) => o.tableId === table.id || o.tableId === table.code,
  );

  // Time counter so seating time refreshes every minute
  const [timeCounter, setTimeCounter] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTimeCounter((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Calculate seating time — same logic as Grid/TableInfo (rounded to the minute)
  const seatingTime = (() => {
    if (!showSeatingTimeInTSR || chequesForTable.length === 0) return undefined;
    const oldest = chequesForTable.reduce((a, b) => (b.createdAt < a.createdAt ? b : a));
    if (!oldest.createdAt) return undefined;
    const diff = DateTime.now()
      .startOf('minute')
      .diff(DateTime.fromISO(oldest.createdAt).startOf('minute'), ['hours', 'minutes']);
    const hours = Math.floor(diff.hours);
    const minutes = Math.floor(diff.minutes);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  })();

  const chequesToMutate = chequesForTable.some((c) => staffCan.mutateCheque(c.id));

  const [{ isOver }, drop] = useDrop({
    accept: [DragTypes.CHEQUE],
    canDrop: (item: any) => {
      return !selected;
    },
    drop: (data) => {
      // Defer the mutation until the dnd drop transaction has completed.
      // Mutating synchronously re-renders and unregisters drop targets
      // mid-transaction, crashing react-dnd with "Expected to find a valid target."
      runDndDrop(useFix, () => {
        api.moveCheque(data.id, table.id);
        const foundCheque = universe.cheques.find((c) => c.id === data?.id);

        bridgeApi.vibrate('impactHeavy');

        onSelectTable(table);

        const ordersForOldtable = orderStatusUniverse.orders.filter(
          (o) => o.tableId === data.table?.id || o.tableId === data.table?.name,
        );

        // Find any order that's on the table and update its tableId to the new table
        for (const o of ordersForOldtable) {
          orderStatusApi.moveOrderToTable(o.id, {
            tableId: table.id,
            tableName: table.code,
          });
        }

        setTimeout(() => {
          if (foundCheque) {
            onSelectCheque(foundCheque);
          }
        }, 300);
      });
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  useEffect(() => {
    if (isOver) {
      bridgeApi.vibrate('textHandleMove');
    }
  }, [isOver]);

  const courseType = courseTypes?.find((ct) => ct.label === currentCourseType);

  // Double-tap detection
  const lastTapRef = useRef<number>(0);
  const doubleTapDelay = 300;

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const now = Date.now();
    if (onDoubleClick && now - lastTapRef.current < doubleTapDelay) {
      lastTapRef.current = 0;
      onDoubleClick(table);
    } else {
      lastTapRef.current = now;
      onTouchEnd(table, keyType);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (window.useTouchInterface) {
      return;
    }
    e.stopPropagation();
    const now = Date.now();
    if (onDoubleClick && now - lastTapRef.current < doubleTapDelay) {
      lastTapRef.current = 0;
      onDoubleClick(table);
    } else {
      lastTapRef.current = now;
      onTouchEnd(table);
    }
  };

  return (
    <Wrapper
      key={table.id}
      ref={(node) => drop(node)}
      active={universe.cheques.some((c) => c.tableId === table.id)}
      selected={selected}
      staffCanMutate={chequesForTable.length === 0 || chequesToMutate}
      hasPrintedCheque={hasPrintedCheque}
      isOver={isOver}
      onTouchStart={onTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
      hasRoundShape={table.rotation === 1 || table.rotation === 4}
      shape={isMapView ? table.rotation : undefined}>
      {courseType && <CourseType color={courseType.color}>{courseType.label}</CourseType>}
      <TableNameWrapper>
        <span>{children}</span>
        {seatingTime && <SeatingTime>{seatingTime}</SeatingTime>}
      </TableNameWrapper>
    </Wrapper>
  );
};

// Use react.memo and log what changes between renders
export default React.memo(Table);
